import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Clock } from "lucide-react";
import { SEO } from "../components/SEO";
import { api } from "../lib/api";

const SPIRITS = ["bourbon", "whiskey", "scotch", "tequila", "vodka", "gin", "rum", "liqueur"];
const DIFF = ["Easy", "Medium", "Hard"];

export default function Recipes() {
    const [params, setParams] = useSearchParams();
    const [items, setItems] = useState([]);
    const spirit = params.get("spirit") || "";
    const difficulty = params.get("difficulty") || "";

    useEffect(() => {
        const opts = { limit: 50 };
        if (spirit) opts.spirit = spirit;
        if (difficulty) opts.difficulty = difficulty;
        api.recipes(opts).then(d => setItems(d.items || []));
    }, [spirit, difficulty]);

    const setP = (k, v) => { const p = new URLSearchParams(params); if (v) p.set(k, v); else p.delete(k); setParams(p); };

    return (
        <>
            <SEO title="Cocktail Recipes" description="Cocktail recipes from Liquor Studio in Clinton, MO. Classic Old Fashioneds to modern Margaritas, with the spirits to make them right." path="/recipes" />

            <section style={{ paddingTop: 140, paddingBottom: 48, borderBottom: "1px solid var(--ls-border)" }}>
                <div className="ls-container">
                    <span className="eyebrow">Recipes</span>
                    <h1 className="font-display mt-4 text-white" style={{ fontSize: "clamp(40px, 6vw, 72px)", fontWeight: 500, lineHeight: 1.02, letterSpacing: "-0.02em" }}>
                        Tonight's <em style={{ fontStyle: "italic", color: "var(--ls-gold)", fontWeight: 400 }}>cocktail</em>
                    </h1>
                </div>
            </section>

            <section className="py-12">
                <div className="ls-container">
                    <div className="flex flex-wrap gap-3 mb-12" data-testid="recipe-filters">
                        <span className="text-xs uppercase tracking-wider self-center" style={{ color: "var(--ls-text-muted)" }}>Spirit:</span>
                        <button onClick={() => setP("spirit", "")} className="btn-ghost" style={{ padding: "5px 12px", fontSize: 12, color: !spirit ? "var(--ls-gold)" : "white", borderColor: !spirit ? "var(--ls-gold)" : "var(--ls-border-strong)" }} data-testid="recipe-spirit-all">All</button>
                        {SPIRITS.map(s => (
                            <button key={s} onClick={() => setP("spirit", s)} className="btn-ghost" style={{ padding: "5px 12px", fontSize: 12, color: spirit === s ? "var(--ls-gold)" : "white", borderColor: spirit === s ? "var(--ls-gold)" : "var(--ls-border-strong)", textTransform: "capitalize" }} data-testid={`recipe-spirit-${s}`}>{s}</button>
                        ))}
                        <span className="text-xs uppercase tracking-wider self-center ml-4" style={{ color: "var(--ls-text-muted)" }}>Difficulty:</span>
                        <button onClick={() => setP("difficulty", "")} className="btn-ghost" style={{ padding: "5px 12px", fontSize: 12, color: !difficulty ? "var(--ls-gold)" : "white", borderColor: !difficulty ? "var(--ls-gold)" : "var(--ls-border-strong)" }}>All</button>
                        {DIFF.map(d => (
                            <button key={d} onClick={() => setP("difficulty", d)} className="btn-ghost" style={{ padding: "5px 12px", fontSize: 12, color: difficulty === d ? "var(--ls-gold)" : "white", borderColor: difficulty === d ? "var(--ls-gold)" : "var(--ls-border-strong)" }} data-testid={`recipe-diff-${d.toLowerCase()}`}>{d}</button>
                        ))}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="recipes-grid">
                        {items.map(r => (
                            <Link key={r.slug} to={`/recipes/${r.slug}`} className="cat-card block" data-testid={`recipe-card-${r.slug}`}>
                                <div style={{ aspectRatio: "4/5", overflow: "hidden" }}>
                                    <img src={r.cover_image_url} alt={r.name} loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                </div>
                                <div className="p-5">
                                    <h3 className="font-display text-white" style={{ fontSize: 22, fontWeight: 500 }}>{r.name}</h3>
                                    <p className="mt-2 text-sm" style={{ color: "var(--ls-text-secondary)", lineHeight: 1.5 }}>{r.description}</p>
                                    <div className="mt-4 flex items-center gap-3 text-xs" style={{ color: "var(--ls-text-muted)" }}>
                                        <span className="flex items-center gap-1"><Clock size={12} />{r.prep_minutes} min</span>
                                        <span>·</span>
                                        <span>{r.difficulty}</span>
                                        <span>·</span>
                                        <span>{r.glassware}</span>
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>
        </>
    );
}
